/**
 * resolveSectionPageColumnCount — per-section page column count.
 *
 * `columnOverride` on SectionConfig forces single/double columns
 * regardless of the page's layout; `inherit` uses the page default.
 *
 * Sections with `supportsColumnLayout: false` in the canonical registry
 * always resolve to a single column unless the template explicitly sets
 * `columnOverride: 'double'`. Without this guard, CSS `column-count: 2` on
 * the section wrapper shrinks card-style sections (e.g. NEPA/CEQA Screening)
 * to half page width.
 */
import type { ReportTemplate, SectionConfig } from '../types/reportTemplate';
import type { ReportTypeId } from '../config/reportTypes';
import { getSectionsForType } from '../config/reportSections';

export function resolveSectionPageColumnCount(
  columnOverride: SectionConfig['columnOverride'] | undefined,
  pageColumns: 1 | 2,
): 1 | 2 {
  if (columnOverride === 'single') return 1;
  if (columnOverride === 'double') return 2;
  return pageColumns;
}

/** Map pageLayouts to a page-number → column-count lookup. */
export function pageColumnsByPageNumber(
  pageLayouts: ReadonlyArray<{ pageNumber: number; columns?: 1 | 2 }> | undefined,
): Map<number, 1 | 2> {
  const map = new Map<number, 1 | 2>();
  for (const pl of pageLayouts ?? []) {
    map.set(pl.pageNumber, pl.columns === 2 ? 2 : 1);
  }
  return map;
}

function buildColumnOverridesFromSections(
  sections: SectionConfig[] | null | undefined,
): Record<string, SectionConfig['columnOverride']> {
  const out: Record<string, SectionConfig['columnOverride']> = {};
  if (!Array.isArray(sections)) return out;
  for (const s of sections) {
    if (s.columnOverride) out[s.sectionId] = s.columnOverride;
  }
  return out;
}

function buildSectionPagesFromSections(
  sections: SectionConfig[] | null | undefined,
): Record<string, number> {
  const out: Record<string, number> = {};
  if (!Array.isArray(sections)) return out;
  for (const s of sections) {
    if (typeof s.page === 'number' && s.page >= 1) {
      out[s.sectionId] = s.page;
    }
  }
  return out;
}

function sectionSupportsColumnLayout(
  sectionId: string,
  reportType: ReportTypeId | undefined | null,
): boolean {
  const def = getSectionsForType(reportType).find(s => s.sectionId === sectionId);
  return def?.supportsColumnLayout ?? false;
}

/** Resolve page column count for one section from template section config.
 *
 * PRECEDENCE (highest → lowest):
 *   0. Registry guard — sections with `supportsColumnLayout: false`
 *      (cover, exec summary, card-style screening sections) are ALWAYS
 *      single column. This keeps the cover / exec-summary lead / wide-figure
 *      sections full width and prevents card sections from being squashed.
 *   1. explicit per-section `columnOverride` (single / double) — user choice;
 *   2. the section's page-layout column count;
 *   3. the theme's `bodyColumns` DEFAULT (two-column academic presets set 2),
 *      applied ONLY to column-capable sections with no explicit page-2 cols
 *      and no override — i.e. genuine prose flow sections (sitingNotes,
 *      climateSevereWeather, …). This is the SAME `data-section-columns`
 *      mechanism, not a parallel one.
 *   4. `defaultPageColumns` (page default; 1 unless the page is two-column).
 *
 * `themeBodyColumns` is OPTIONAL ⇒ absent reduces EXACTLY to the prior
 * behaviour, so legacy (no-preset) reports are byte-stable.
 */
export function resolveSectionPageColumnCountForSection(
  sectionId: string,
  columnOverrides: Record<string, SectionConfig['columnOverride']> | undefined,
  sectionPages: Record<string, number> | undefined,
  pageLayouts: ReportTemplate['pageLayouts'],
  defaultPageColumns: 1 | 2 = 1,
  reportType?: ReportTypeId | null,
  themeBodyColumns?: 1 | 2,
): 1 | 2 {
  // Registry guard wins over template overrides + theme default — card-style
  // sections (NEPA/CEQA, exec summary, etc.) must not inherit page
  // `column-count: 2` even when a template stamps `columnOverride: 'double'`
  // or a two-column preset is selected. This is what keeps the cover, the
  // executive-summary lead, and wide-figure sections full width.
  if (!sectionSupportsColumnLayout(sectionId, reportType)) return 1;

  // #163 — a single-column PRESET is AUTHORITATIVE: a theme that declares
  // `bodyColumns: 1` (Journal / Institutional / Monograph) forces every section
  // to a single column, regardless of any template `columnOverride: 'double'`
  // or two-column `pageLayouts`. Without this, a template that stamps a 2-col
  // page/section overrode the preset and a 1-column theme rendered 2-column
  // content (e.g. the TOC). `themeBodyColumns` is OPTIONAL — when absent
  // (legacy / no-preset callers) this is skipped and the prior template-driven
  // precedence below applies unchanged, so legacy reports stay byte-stable.
  if (themeBodyColumns === 1) return 1;

  const override = columnOverrides?.[sectionId];
  if (override === 'single') return 1;
  if (override === 'double') return 2;

  const pageColMap = pageColumnsByPageNumber(pageLayouts);
  const pageNum = sectionPages?.[sectionId] ?? 1;
  const pageCols = pageColMap.get(pageNum);
  if (pageCols != null) return pageCols;

  // No explicit page-layout columns for this section — fall back to the
  // theme's two-column DEFAULT (academic presets) when set, else the page
  // default (1 unless the page itself is two-column).
  if (themeBodyColumns === 2) return 2;
  return defaultPageColumns;
}

/**
 * Page-level two-column FLOW classification for a section (#93, reconciled
 * with the #159 per-section islands).
 *
 *   - "island" — the legacy per-section 2-column island (#159). Each section
 *     owns its own `column-count: 2` box and its heading spans that box. This
 *     is the DEFAULT for any non-page-flow context: single-column presets, the
 *     continuous (non-paged) fallback, and the screen-print mirror.
 *   - "flow"   — a flow-safe block that packs into the *page*-level column
 *     context (one continuous two-column flow across the whole page).
 *     Rendered `break-inside: avoid` with an INLINE heading (heading does NOT
 *     span both columns; it flows with its prose).
 *   - "span"   — a wide block (chart-only / card / wide-figure section) that
 *     spans BOTH page columns (`column-span: all`) inside the page flow.
 *
 * The classification is driven by the SAME `supportsColumnLayout` registry
 * flag the column-count resolver already uses (RULE: no parallel mechanism):
 * column-capable prose → "flow"; non-column-capable wide/chart/card sections
 * → "span". When `pageFlowActive` is false (no page-level flow) everything is
 * an "island" — this is what keeps single-column / continuous-fallback reports
 * byte-stable (the caller stamps NOTHING when not in a two-column preset).
 */
export type SectionFlowMode = 'island' | 'flow' | 'span';

export function resolveSectionFlowModeForSection(
  sectionId: string,
  pageFlowActive: boolean,
  reportType?: ReportTypeId | null,
): SectionFlowMode {
  // Without page-level flow, every section is a legacy per-section island
  // (#159). This is the continuous-fallback + single-column behaviour and
  // keeps the existing heading-span:all island rule in force.
  if (!pageFlowActive) return 'island';

  // In page-level flow, wide/chart/card sections (cover, exec-summary lead,
  // radar/LCOE/climate charts, pathway comparison, NEPA/CEQA cards, …) cannot
  // be packed into a narrow column without squashing — they span both columns.
  // Genuine prose flow sections pack into the page column context.
  return sectionSupportsColumnLayout(sectionId, reportType) ? 'flow' : 'span';
}

export { buildColumnOverridesFromSections, buildSectionPagesFromSections };
